import React, { useState, useEffect } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    Cell
} from 'recharts';
import { getDashboardStats, getDropOffStats, getCategoryStats, getRecentEstimates } from '../../../api/autoEstimate';
import AdminLayout from '../../../components/admin/AdminLayout';
import AdminButton from '../../../components/admin/AdminButton';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const AnalyticsDashboard = () => {
    const [period, setPeriod] = useState('1w');
    const [stats, setStats] = useState(null);
    const [dropOffData, setDropOffData] = useState([]);
    const [categoryData, setCategoryData] = useState([]);
    const [recentList, setRecentList] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, [period]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [sData, dData, cData, rData] = await Promise.all([
                getDashboardStats(period),
                getDropOffStats(),
                getCategoryStats(),
                getRecentEstimates()
            ]);
            setStats(sData);
            setDropOffData(dData);
            setCategoryData(cData);
            setRecentList(rData);
        } catch (error) {
            console.error('Failed to load analytics', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (val) => {
        if (!val) return '0원';
        if (val >= 100000000) return `${(val / 100000000).toFixed(1)}억원`;
        if (val >= 10000) return `${(val / 10000).toLocaleString()}만원`;
        return `${val.toLocaleString()}원`;
    };

    return (
        <AdminLayout>
            <div className="w-full">
                <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <h1 className="text-2xl font-extrabold text-gray-800 m-0 tracking-tight">자동견적 현황 (Analytics)</h1>
                    <div className="flex flex-wrap gap-2">
                        {['1w', '1m', '3m', '6m'].map(p => (
                            <AdminButton
                                key={p}
                                variant={period === p ? 'primary' : 'secondary'}
                                size="sm"
                                onClick={() => setPeriod(p)}
                            >
                                {p === '1w' ? '1주' : p === '1m' ? '1개월' : p === '3m' ? '3개월' : '6개월'}
                            </AdminButton>
                        ))}
                    </div>
                </header>

                {loading ? (
                    <div className="p-12 text-center text-gray-500">데이터 분석 중...</div>
                ) : (
                    <div className="dashboard-content w-full flex flex-col gap-5">

                        {/* KPI Cards */}
                        <div className="kpi-row grid grid-cols-1 md:grid-cols-3 gap-5">
                            {/* Card 1: Engagement */}
                            <div className="kpi-card bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-center">
                                <h4 className="text-sm font-semibold text-gray-500 mb-3 m-0">총 견적 시도 (Traffic)</h4>
                                <div className="text-3xl font-bold text-gray-900 mb-2">
                                    {stats?.totalSessions?.toLocaleString()} <span className="text-sm font-normal text-gray-500">건</span>
                                </div>
                                <div className={`text-xs ${stats?.completedSessions > 0 ? 'text-emerald-600' : 'text-gray-500'}`}>
                                    완료율: <span className="font-bold">{stats?.rates?.completion}%</span> ({stats?.completedSessions}건 완료)
                                </div>
                            </div>

                            {/* Card 2: Conversion (Direct) */}
                            <div className="kpi-card bg-white p-5 rounded-xl border-l-4 border-l-blue-500 border-y border-r border-gray-200 shadow-sm flex flex-col justify-center">
                                <h4 className="text-sm font-semibold text-gray-500 mb-3 m-0">직접 문의 전환 (Direct Lead)</h4>
                                <div className="text-3xl font-bold text-blue-600 mb-2">
                                    {stats?.conversions} <span className="text-sm font-normal text-gray-500">건</span>
                                </div>
                                <div className="text-xs text-gray-500">
                                    전환율: <span className="font-bold text-gray-700">{stats?.rates?.conversion}%</span> (완료 대비)
                                </div>
                            </div>

                            {/* Card 3: Value */}
                            <div className="kpi-card bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-center">
                                <h4 className="text-sm font-semibold text-gray-500 mb-3 m-0">잠재 매출 파이프라인</h4>
                                <div className="text-3xl font-bold text-emerald-600 mb-2">
                                    {formatCurrency(stats?.totalQuotedValue)}
                                </div>
                                <div className="text-xs text-gray-500">
                                    문의 금액: <span className="font-bold text-gray-700">{formatCurrency(stats?.inquiryValue)}</span> ({stats?.rates?.realization}%)
                                </div>
                            </div>
                        </div>

                        {/* Charts Row */}
                        <div className="charts-row grid grid-cols-1 xl:grid-cols-2 gap-5 w-full">
                            {/* Drop-off Chart */}
                            <div className="chart-box bg-white p-5 rounded-xl border border-gray-200 shadow-sm w-full min-w-0">
                                <h3 className="text-lg font-bold text-gray-800 mb-2">이탈 분석 (Drop-off Analysis)</h3>
                                <p className="text-xs text-gray-500 mb-5">가장 마지막으로 조회한 질문입니다. (값이 높을수록 해당 질문에서 이탈함)</p>
                                <div className="h-[300px] w-full">
                                    <ResponsiveContainer>
                                        <BarChart
                                            layout="vertical"
                                            data={dropOffData}
                                            margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                                            <XAxis type="number" />
                                            <YAxis dataKey="shortLabel" type="category" width={80} tick={{ fontSize: 11 }} />
                                            <Tooltip
                                                wrapperStyle={{ maxWidth: '350px', zIndex: 100 }}
                                                contentStyle={{
                                                    fontSize: '13px',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    maxWidth: '330px'
                                                }}
                                                labelStyle={{
                                                    fontSize: '13px',
                                                    fontWeight: 'bold',
                                                    marginBottom: '4px',
                                                    display: 'block',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word'
                                                }}
                                                formatter={(value, name, props) => [value, '이탈 수']}
                                                labelFormatter={(label, payload) => {
                                                    if (payload && payload[0]) {
                                                        return payload[0].payload.questionText;
                                                    }
                                                    return label;
                                                }}
                                            />
                                            <Bar dataKey="count" name="이탈 수" fill="#ff8042" radius={[0, 4, 4, 0]} barSize={20} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Category Chart - Horizontal Bar */}
                            <div className="chart-box bg-white p-5 rounded-xl border border-gray-200 shadow-sm w-full min-w-0">
                                <h3 className="text-lg font-bold text-gray-800 mb-5">견적 유형 분포 (Category)</h3>
                                <div className="h-[300px] w-full">
                                    <ResponsiveContainer>
                                        <BarChart
                                            layout="vertical"
                                            data={categoryData}
                                            margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                                            <XAxis type="number" />
                                            <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 10 }} />
                                            <Tooltip />
                                            <Bar dataKey="count" name="완료 건수" radius={[0, 4, 4, 0]} barSize={18}>
                                                {categoryData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>

                        {/* Recent List */}
                        <div className="recent-list-box bg-white p-5 rounded-xl border border-gray-200 shadow-sm w-full">
                            <h3 className="text-lg font-bold text-gray-800 mb-5">최근 완료된 견적</h3>
                            
                            {/* Desktop Table */}
                            <div className="hidden md:block w-full overflow-x-auto">
                                <table className="w-full border-collapse text-sm">
                                    <thead>
                                        <tr className="border-b-2 border-gray-200 text-gray-600">
                                            <th className="p-3 text-left">일시</th>
                                            <th className="p-3 text-left">유형</th>
                                            <th className="p-3 text-right">최종 견적가</th>
                                            <th className="p-3 text-center">상태</th>
                                            <th className="p-3 text-center">문의 여부</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recentList.map(item => (
                                            <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                                                <td className="p-3 text-gray-600">{new Date(item.date).toLocaleString()}</td>
                                                <td className="p-3">
                                                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs">{item.category}</span>
                                                </td>
                                                <td className="p-3 text-right font-bold text-gray-800">{item.amount.toLocaleString()}원</td>
                                                <td className="p-3 text-center">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.status === 'SUBMITTED' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}`}>
                                                        {item.status}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-center">
                                                    {item.hasInquiry ? <span className="text-blue-600 font-bold">✅ 접수</span> : <span className="text-gray-300">-</span>}
                                                </td>
                                            </tr>
                                        ))}
                                        {recentList.length === 0 && (
                                            <tr><td colSpan="5" className="p-8 text-center text-gray-500">데이터가 없습니다.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Card View */}
                            <div className="grid grid-cols-1 gap-4 md:hidden">
                                {recentList.map(item => (
                                    <div key={item.id} className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex flex-col gap-3">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <span className="px-2 py-1 bg-white border border-gray-200 text-gray-700 rounded-md text-xs mb-2 inline-block shadow-sm">{item.category}</span>
                                                <div className="text-xs text-gray-500">{new Date(item.date).toLocaleString()}</div>
                                            </div>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.status === 'SUBMITTED' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-700'}`}>
                                                {item.status}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-end border-t border-gray-200 mt-2 pt-3">
                                            <div className="text-sm">
                                                {item.hasInquiry ? <span className="text-blue-600 font-bold bg-blue-50 px-2 py-1 rounded-md text-xs">✅ 문의 접수됨</span> : <span className="text-gray-400 text-xs">문의 없음</span>}
                                            </div>
                                            <div className="text-lg font-bold text-gray-900">{item.amount.toLocaleString()}원</div>
                                        </div>
                                    </div>
                                ))}
                                {recentList.length === 0 && (
                                    <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-xl">데이터가 없습니다.</div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default AnalyticsDashboard;
